import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface MetaDetails {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  keywords?: string;
  image?: string;
  noindex?: boolean;
}

const ECOSYSTEM_DOMAINS = [
  "https://flamesnova.name.ng",
  "https://vtu.flamesnova.name.ng",
  "https://ai.flamesnova.name.ng",
  "https://store.flamesnova.name.ng",
];

export const getSubdomainInfo = () => {
  if (typeof window === "undefined") {
    return { isVtu: false, isAi: false, isStore: false, isParent: true, siteUrl: "https://flamesnova.name.ng", brandName: "Flames Nova" };
  }
  const host = window.location.hostname.toLowerCase();
  const isVtu = host.startsWith("vtu.") || host === "vtu.flamesnova.name.ng";
  const isAi = host.startsWith("ai.") || host === "ai.flamesnova.name.ng";
  const isStore = host.startsWith("store.") || host === "store.flamesnova.name.ng";
  const isParent = !isVtu && !isAi && !isStore;

  const siteUrl = isVtu 
    ? "https://vtu.flamesnova.name.ng"
    : isAi
    ? "https://ai.flamesnova.name.ng"
    : isStore
    ? "https://store.flamesnova.name.ng"
    : "https://flamesnova.name.ng";

  const brandName = isVtu
    ? "Flames Nova VTU"
    : isAi
    ? "Flames Nova AI"
    : isStore
    ? "FN Atelier Store"
    : "Flames Nova";

  return { isVtu, isAi, isStore, isParent, siteUrl, brandName };
};

// ─── Subdomain Specific Metadata Maps ──────────────────────────────────

const VTU_META: Record<string, MetaDetails> = {
  "/": {
    title: "Buy Data & Airtime Online in Nigeria | Flames Nova VTU",
    description: "Buy supported airtime and mobile data bundles through Flames Nova VTU. Explore available networks, check current prices and top up online.",
    ogTitle: "Flames Nova VTU — Airtime & Data",
    ogDescription: "Explore supported airtime and data services, check available bundles and complete your top-up online.",
    keywords: "buy data Nigeria, buy airtime online, MTN data, Airtel data, Glo data, 9mobile data, Flames Nova VTU, electricity tokens, cable subscriptions",
    image: "/brands/flames-nova-vtu-icon.png",
  },
  "/services/data": {
    title: "Buy Cheap SME Data Bundles (MTN, Airtel, Glo, 9mobile) | Flames Nova VTU",
    description: "Instant SME, corporate gifting, and direct data bundles starting from ₦140 with automated sub-second delivery 24/7.",
    keywords: "buy cheap data Nigeria, MTN SME data 1GB, Airtel cheap data, Glo cheap data 2026",
    image: "/brands/flames-nova-vtu-icon.png",
  },
  "/services/airtime": {
    title: "Buy Discounted Airtime Online | MTN, Airtel, Glo, 9mobile | Flames Nova VTU",
    description: "Instant airtime top-up for all Nigerian networks with automated delivery and up to 3% discount.",
    keywords: "buy airtime online Nigeria, discount airtime, MTN recharge card online",
  },
  "/airtime-to-cash": {
    title: "Convert Airtime to Instant Cash in Nigeria | Flames Nova VTU",
    description: "Convert excess MTN, Airtel, Glo and 9mobile airtime into real cash deposited into your Nigerian bank account or Flames Nova wallet instantly.",
    keywords: "airtime to cash, convert airtime to money, airtime to bank transfer Nigeria",
    image: "/brands/flames-nova-vtu-icon.png",
  },
  "/airtime-to-cash-calculator": {
    title: "Airtime to Cash Calculator 2026 — Check Instant Payout Rates | Flames Nova VTU",
    description: "Free public airtime to cash conversion calculator. Check live conversion rates across MTN, Airtel, Glo and 9mobile before converting.",
    keywords: "airtime to cash calculator, airtime conversion rate Nigeria",
  },
  "/compare-airtime-to-cash": {
    title: "Compare Airtime to Cash Rates Nigeria 2026 | Flames Nova VTU",
    description: "Compare airtime to cash conversion fees and payout speeds across top Nigerian platforms. Get the highest payout on Flames Nova VTU.",
  },
  "/services/electricity": {
    title: "Buy Prepaid Electricity Tokens Online (All Discos) | Flames Nova VTU",
    description: "Instant prepaid meter token generation for EKEDC, IKEDC, AEDC, PHED, IBEDC and all Nigerian electricity distribution companies.",
    keywords: "buy electricity token online, prepaid meter recharge, NEPA bill payment",
  },
  "/services/cable": {
    title: "Renew DStv, GOtv & Startimes Subscriptions Online | Flames Nova VTU",
    description: "Instant activation for DStv, GOtv, and Startimes decoders with automatic IUC/smartcard verification and zero convenience fee.",
    keywords: "dstv subscription online, gotv recharge, startimes renew Nigeria",
  },
  "/services/sms-verify": {
    title: "SMS OTP Verification Numbers Online | Flames Nova VTU",
    description: "Rent virtual temporary phone numbers for SMS and OTP verification across global services paid in Nigerian Naira.",
    keywords: "SMS verify Nigeria, buy virtual number, OTP SMS receiver",
  },
  "/services/exam-pins": {
    title: "Buy WAEC, NECO & NABTEB Result Checker PINs | Flames Nova VTU",
    description: "Buy instant WAEC result checker PINs, NECO tokens, and NABTEB scratch cards online. Instant delivery to your dashboard.",
    keywords: "buy WAEC scratch card online, NECO result token buy, NABTEB result checker pin",
  },
  "/docs": {
    title: "VTU API & Webhook Documentation — Telecom Integration | Flames Nova VTU",
    description: "Automate your telecom vending with Flames Nova VTU Developer API. Sub-second data delivery, real-time balance webhooks, and REST endpoints.",
    keywords: "VTU API Nigeria, telecom vending API, airtime API integration",
  },
  "/about": {
    title: "About Flames Nova VTU — Automated Telecom & Utility Infrastructure",
    description: "Learn about Flames Nova VTU, founded by Agbasionwe Emmanuel Chiemelie to provide reliable, automated digital telecom utilities in Nigeria.",
  },
};

const AI_META: Record<string, MetaDetails> = {
  "/": {
    title: "Flames Nova AI | AI Assistant for Research & Coding",
    description: "Use Flames Nova AI for research, coding, writing and other practical tasks with the AI capabilities available on the platform.",
    ogTitle: "Flames Nova AI — Research, Coding & Productivity",
    ogDescription: "Explore practical AI tools for research, coding, writing and everyday work.",
    keywords: "Flames Nova AI, AI assistant, coding assistant, AI research, writing tools, Apex R1, Prism Coder",
    image: "/brands/flames-nova-ai-icon.png",
  },
  "/chat": {
    title: "Chat with Flames Nova AI — Deep Reasoning & Analysis",
    description: "Start a conversation with Apex R1 or Prism Coder. Deep multi-step reasoning, real-time web intelligence, and code generation.",
  },
  "/pricing": {
    title: "Flames Nova AI Pricing — Free & Pro Plans Paid in Naira | ₦25,000/mo",
    description: "Flames Nova AI pricing in Naira. Free tier with 30 messages daily. Pro tier with unlimited access, Deep Research, 17 SEO tools, and priority inference.",
    keywords: "Flames Nova AI pricing, Nigerian AI subscription, Paystack AI payment",
    image: "/brands/flames-nova-ai-icon.png",
  },
  "/docs": {
    title: "Flames Nova AI & MCP Developer Documentation | Model Context Protocol",
    description: "Official developer guide for Flames Nova AI, Anthropic Model Context Protocol (MCP) servers, REST endpoints, and custom agent tools.",
    keywords: "Flames Nova AI docs, Model Context Protocol, MCP server Nigeria, AI API",
  },
  "/use-cases": {
    title: "What is Flames Nova AI Used For? | 10+ Enterprise & Student Use Cases",
    description: "Explore how Nigerians use Flames Nova AI for software engineering, academic research, corporate FIRS tax computation, and automated content creation.",
  },
  "/comparison": {
    title: "Flames Nova AI vs ChatGPT vs Claude vs Gemini — Full 2026 Review",
    description: "Compare Flames Nova AI against ChatGPT, Claude, and Gemini. Discover why local Nigerian context, Naira billing, and zero API restrictions make it superior.",
  },
};

const STORE_META: Record<string, MetaDetails> = {
  "/": {
    title: "Buy Verified Electronics & Tech Gadgets Online | Flames Nova Store",
    description: "Shop verified smartphones, laptops, audio equipment and accessories on Flames Nova Store. Browse available products, check specifications and order online.",
    ogTitle: "Flames Nova Store — Tech Gadgets & Electronics",
    ogDescription: "Explore verified tech gadgets, laptops, audio and accessories with direct ordering and clear delivery information.",
    keywords: "Flames Nova Store, buy electronics online Nigeria, smartphones, laptops, Starlink, audio accessories, gadgets Lagos",
    image: "/brands/fn-atelier-icon.png",
  },
  "/search": {
    title: "Search Curated Tech Hardware & Audio | FN Atelier",
    description: "Search high-end mobile gear, GaN charging architecture, Starlink accessories, and studio acoustics.",
  },
  "/cart": {
    title: "Your Shopping Bag | FN Atelier",
    description: "Review selected luxury tech items and proceed to secure checkout with nationwide insured delivery.",
    noindex: true,
  },
  "/checkout": {
    title: "Secure Checkout — Escrow & Card Payment | FN Atelier",
    description: "Complete your hardware purchase securely with Paystack escrow protection.",
    noindex: true,
  },
  "/docs": {
    title: "FN Atelier Developer Documentation — Agentic Commerce Protocols (ACP/UCP)",
    description: "Technical specifications for machine-readable shopping protocols (ACP), inventory negotiation (UCP), and catalog APIs.",
    keywords: "Agentic Commerce Protocol, UCP protocol, FN Atelier developer API",
  },
};

const PARENT_META: Record<string, MetaDetails> = {
  "/": {
    title: "Flames Nova | AI Tools, VTU Services & Technology Products",
    description: "Explore Flames Nova's growing technology ecosystem, including AI tools for research and coding, airtime and data services, utility payments, and technology products.",
    ogTitle: "Flames Nova | AI Tools, VTU Services & Technology Products",
    ogDescription: "Explore Flames Nova's growing technology ecosystem, including AI tools for research and coding, airtime and data services, utility payments, and technology products.",
    keywords: "Flames Nova, technology ecosystem Nigeria, digital products, AI tools, telecom infrastructure, online services, Agbasionwe Emmanuel Chiemelie",
    image: "/brands/flames-nova-icon.png",
  },
  "/docs": {
    title: "Ecosystem Architecture & Developer Protocol Docs | Flames Nova",
    description: "Technical overview of the Flames Nova ecosystem, unified Single Sign-On (SSO), RFC 9727 API Linkset, and autonomous agent protocols.",
    keywords: "Flames Nova docs, ecosystem architecture, developer protocols Nigeria",
  },
  "/news": {
    title: "News Hub — Breaking & Fact-Checked News Briefs | Flames Nova",
    description: "Discover curated, fact-checked news briefs across Technology, Business, World News, Artificial Intelligence, and Africa on Flames Nova.",
  },
  "/news/technology": {
    title: "Technology News & Innovations | Flames Nova News",
    description: "Latest technology news, software updates, AI breakthroughs, and consumer tech developments summarized with clear analysis.",
  },
  "/news/business": {
    title: "Business & Economy News | Flames Nova News",
    description: "Curated business insights, financial markets, startup ecosystem updates, and economic trends on Flames Nova.",
  },
  "/news/world": {
    title: "World News & Global Affairs | Flames Nova News",
    description: "Verified international news reports, geopolitical developments, and global event summaries.",
  },
  "/news/ai": {
    title: "Artificial Intelligence News & Research | Flames Nova News",
    description: "Latest AI product releases, machine learning research, LLMs, and robotics news with editorial context.",
  },
  "/news/africa": {
    title: "Africa News & Innovations | Flames Nova News",
    description: "Breaking news, tech ecosystem growth, business developments, and economic reports from across Africa.",
  },
  "/news/entertainment": {
    title: "Entertainment & Culture News | Flames Nova News",
    description: "Film, music, streaming, and culture industry news verified with primary source attribution.",
  },
  "/news/sports": {
    title: "Sports News & Match Coverage | Flames Nova News",
    description: "Latest sports updates, football, athletics, tournament results, and major announcements.",
  },
  "/news/science": {
    title: "Science & Health News | Flames Nova News",
    description: "Medical breakthroughs, space exploration, and scientific research summarized on Flames Nova.",
  },
  "/news/climate": {
    title: "Climate & Environment News | Flames Nova News",
    description: "Clean energy, climate resilience, and conservation reporting with editorial analysis.",
  },
  "/blog": {
    title: "Flames Nova Blog — Engineering, AI & Fintech Insights",
    description: "In-depth engineering notes, fintech architecture, AI research, and ecosystem updates from the Flames Nova team.",
  },
  "/about": {
    title: "About Flames Nova — Technology & Infrastructure Mission",
    description: "Founded by Agbasionwe Emmanuel Chiemelie, Flames Nova builds foundational digital infrastructure for Africa.",
  },
  "/auth": {
    title: "Flames Nova SSO — Sign In or Create Account",
    description: "One single account for Flames Nova VTU, Flames Nova AI, and FN Atelier Store. Sign in or register in seconds.",
    noindex: true,
  },
  "/legal": {
    title: "Legal Centre — Unified Policies & Compliance | Flames Nova",
    description: "Official terms of service, privacy policy, NDPR compliance, refund policies, and security architecture across Flames Nova.",
  },
  "/legal/terms": {
    title: "Terms of Service | Flames Nova Ecosystem",
    description: "Universal terms of service governing all Flames Nova subdomains, wallets, APIs, and commercial services.",
  },
  "/legal/privacy": {
    title: "Privacy Policy & NDPR Compliance | Flames Nova",
    description: "How Flames Nova processes and protects personal information across all platforms under the Nigeria Data Protection Regulation.",
  },
  "/legal/compliance": {
    title: "Corporate Compliance & Regulatory Framework | Flames Nova",
    description: "Regulatory compliance standards including NCC guidelines, NDPR compliance, AML/CFT rules, and merchant integrity.",
  },
  "/legal/security": {
    title: "Security Architecture & Trust Policy | Flames Nova",
    description: "Defense-in-depth security, HMAC signature verification, zero-trust SSO architecture, and responsible disclosure.",
  },
};

export const SEOMeta = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const { isVtu, isAi, isStore, isParent, siteUrl, brandName } = getSubdomainInfo();

    // Skip blog posts and dynamic articles (they manage their own metadata)
    const staticNewsPaths = ["/news/technology", "/news/business", "/news/world", "/news/ai", "/news/africa", "/news/entertainment", "/news/sports", "/news/science", "/news/climate"];
    if (
      path === "/weird-websites" ||
      path.startsWith("/blog/") || 
      path.startsWith("/articles/") || 
      path.startsWith("/article/") || 
      path.startsWith("/AI/") ||
      path.startsWith("/ai/preview/") ||
      (path.startsWith("/news/") && !staticNewsPaths.includes(path))
    ) return;

    // Pick active dictionary based on subdomain
    const activeMap = isVtu 
      ? VTU_META 
      : isAi 
      ? AI_META 
      : isStore 
      ? STORE_META 
      : PARENT_META;

    // Match exact path or prefix match
    let meta = activeMap[path];
    if (!meta) {
      const prefixMatch = Object.keys(activeMap).find(
        (key) => key !== "/" && path.startsWith(key + "/")
      );
      if (prefixMatch) meta = activeMap[prefixMatch];
    }

    // Fallback to default for this subdomain if not matched
    if (!meta) {
      meta = activeMap["/"] || {
        title: `${brandName} — Official Platform`,
        description: `Welcome to ${brandName}, an essential part of the Flames Nova technology ecosystem.`,
      };
    }

    const currentTitle = meta.title;
    const currentDesc = meta.description;
    const currentCanonical = `${siteUrl}${path === "/" ? "" : path}`;
    const currentImage = meta.image 
      ? `${siteUrl}${meta.image}`
      : isVtu 
      ? `${siteUrl}/brands/flames-nova-vtu-icon.png`
      : isAi
      ? `${siteUrl}/brands/flames-nova-ai-icon.png`
      : isStore
      ? `${siteUrl}/brands/fn-atelier-icon.png`
      : `${siteUrl}/brands/flames-nova-icon.png`;

    // ─── Update Head DOM ───────────────────────────────────────────
    document.title = currentTitle;

    // Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", currentDesc);

    // Keywords
    if (meta.keywords) {
      let metaKw = document.querySelector('meta[name="keywords"]');
      if (!metaKw) {
        metaKw = document.createElement("meta");
        metaKw.setAttribute("name", "keywords");
        document.head.appendChild(metaKw);
      }
      metaKw.setAttribute("content", meta.keywords);
    }

    // Robots
    let robots = document.head.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", meta.noindex ? "noindex, follow" : "index, follow");

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", currentCanonical);

    // Favicon
    const brandFavicon = isVtu 
      ? "/brands/flames-nova-vtu-favicon.png"
      : isAi
      ? "/brands/flames-nova-ai-favicon.png"
      : isStore
      ? "/brands/fn-atelier-favicon.png"
      : "/brands/flames-nova-favicon.png";

    let favicon = document.querySelector('link[rel="icon"]');
    if (!favicon) {
      favicon = document.createElement("link");
      favicon.setAttribute("rel", "icon");
      document.head.appendChild(favicon);
    }
    favicon.setAttribute("href", brandFavicon);

    // Open Graph
    const setMeta = (prop: string, val: string) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", prop);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };

    setMeta("og:title", meta.ogTitle || currentTitle);
    setMeta("og:description", meta.ogDescription || currentDesc);
    setMeta("og:url", currentCanonical);
    setMeta("og:image", currentImage);
    setMeta("og:site_name", brandName);
    setMeta("og:type", "website");

    // Twitter Card
    const setTwitter = (name: string, val: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };

    setTwitter("twitter:card", "summary_large_image");
    setTwitter("twitter:title", meta.ogTitle || currentTitle);
    setTwitter("twitter:description", meta.ogDescription || currentDesc);
    setTwitter("twitter:image", currentImage);

    // ─── Linked Data (JSON-LD) for Search Authority Cycle ──────────
    let schemaGraph = document.querySelector('script[data-schema="ecosystem-cycle-graph"]');
    if (!schemaGraph) {
      schemaGraph = document.createElement("script");
      schemaGraph.setAttribute("type", "application/ld+json");
      schemaGraph.setAttribute("data-schema", "ecosystem-cycle-graph");
      document.head.appendChild(schemaGraph);
    }

    schemaGraph.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          "url": siteUrl,
          "name": brandName,
          "description": currentDesc,
          "publisher": {
            "@id": "https://flamesnova.name.ng/#organization"
          },
          "isPartOf": {
            "@id": "https://flamesnova.name.ng/#ecosystem"
          }
        },
        {
          "@type": "Organization",
          "@id": "https://flamesnova.name.ng/#organization",
          "name": "Flames Nova",
          "url": "https://flamesnova.name.ng",
          "logo": "https://flamesnova.name.ng/brands/flames-nova-icon.png",
          "founder": {
            "@type": "Person",
            "name": "Agbasionwe Emmanuel Chiemelie"
          },
          "sameAs": ECOSYSTEM_DOMAINS,
          "subOrganization": [
            {
              "@type": "Organization",
              "name": "Flames Nova VTU",
              "url": "https://vtu.flamesnova.name.ng"
            },
            {
              "@type": "Organization",
              "name": "Flames Nova AI",
              "url": "https://ai.flamesnova.name.ng"
            },
            {
              "@type": "Organization",
              "name": "FN Atelier Store",
              "url": "https://store.flamesnova.name.ng"
            }
          ]
        }
      ]
    });

  }, [location.pathname]);

  return null;
};

export default SEOMeta;
